import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useChatAI } from "./chat-ai-context";
import { Input } from "@/components/base/input/input";
import { Button } from "@/components/base/buttons/button";
import { CloseButton } from "@/components/base/buttons/close-button";
import { useTranslation } from "react-i18next";

export const AIFloatingPrompt = () => {
    const { t } = useTranslation(['common']);
    const { isPromptOpen, promptPosition, closeFloatingPrompt, submitPrompt } = useChatAI();
    const [text, setText] = useState("");
    const inputRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        if (isPromptOpen) {
            // small delay to ensure element is mounted
            const t = setTimeout(() => inputRef.current?.focus(), 0);
            return () => clearTimeout(t);
        } else {
            setText("");
        }
    }, [isPromptOpen]);

    if (!isPromptOpen || !promptPosition) return null;

    const style: React.CSSProperties = {
        position: "fixed",
        left: Math.min(Math.max(8, promptPosition.x - 160), window.innerWidth - 320 - 8),
        top: Math.min(Math.max(8, promptPosition.y - 24), window.innerHeight - 48 - 8),
        width: 320,
        zIndex: 60,
    };

    const onSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const value = text.trim();
        if (!value) return;
        submitPrompt(value);
        setText("");
    };

    return createPortal(
        <div style={style} className="rounded-md border border-secondary bg-primary p-2 shadow-lg">
            <div className="flex items-center justify-between pb-1 pl-1 pr-1">
                <span className="text-xs text-tertiary">{t('aiChat.title')}</span>
                <CloseButton size="sm" onClick={closeFloatingPrompt} />
            </div>
            <form onSubmit={onSubmit} className="flex items-center gap-2">
                <Input
                    ref={inputRef}
                    value={text}
                    onChange={(val) => setText(val as string)}
                    placeholder={t('aiChat.placeholder')}
                    aria-label={t('aiChat.ariaLabel')}
                    className="flex-1"
                />
                <Button color="primary" size="sm" type="submit">
                    {t('buttons.submit')}
                </Button>
            </form>
        </div>,
        document.body,
    );
};


